// A picture's aspect ratio, snapped to the common ones ("3:2", "2:3", "1:1"…). Shared by the album
// grid's cards, the viewer's info panel and the aspect ratio filter.

import {DriveFile, isImageFile} from '../drive/types';

export interface Dimensions {
    width: number;
    height: number;
}

interface NamedRatio {
    /** Width:height, as shown (and used as the filter value). */
    name: string;
    ratio: number;
}

const LANDSCAPE_RATIOS: NamedRatio[] = [
    {name: '5:4', ratio: 5 / 4},
    {name: '4:3', ratio: 4 / 3},
    {name: '3:2', ratio: 3 / 2},
    {name: '16:9', ratio: 16 / 9},
];

/** Common ratios in both orientations, plus square. */
const NAMED_RATIOS: NamedRatio[] = [
    {name: '1:1', ratio: 1},
    ...LANDSCAPE_RATIOS,
    ...LANDSCAPE_RATIOS.map(({name, ratio}) => ({name: name.split(':').reverse().join(':'), ratio: 1 / ratio})),
];

/** How far (relatively) a picture's ratio may be from a named one and still count as it — absorbs crop rounding. */
const RATIO_TOLERANCE = 0.02;

export const OTHER_LANDSCAPE_RATIO = 'other-landscape';
export const OTHER_PORTRAIT_RATIO = 'other-portrait';

/**
 * The picture's displayed dimensions; `null` for non-images and without dimensions. Drive's are of
 * the stored image, so a quarter `rotation` swaps them.
 */
export function displayedDimensions(file: DriveFile): Dimensions | null {
    const meta = file.imageMediaMetadata;
    if (!isImageFile(file) || !meta?.width || !meta?.height) return null;
    const quarterTurned = Math.abs(meta.rotation ?? 0) % 2 === 1;
    return quarterTurned ? {width: meta.height, height: meta.width} : {width: meta.width, height: meta.height};
}

function namedRatio({width, height}: Dimensions): NamedRatio | undefined {
    const ratio = width / height;
    return NAMED_RATIOS.find((r) => Math.abs(ratio / r.ratio - 1) <= RATIO_TOLERANCE);
}

/** Width:height snapped to a common ratio ("3:2", "2:3", "1:1"…), or "other" per orientation. */
export function snapAspectRatio(dimensions: Dimensions): string {
    const named = namedRatio(dimensions);
    if (named) return named.name;
    return dimensions.width > dimensions.height ? OTHER_LANDSCAPE_RATIO : OTHER_PORTRAIT_RATIO;
}

/** The picture's displayed ratio snapped by `snapAspectRatio`; `null` without dimensions. */
export function aspectRatioOf(file: DriveFile): string | null {
    const dimensions = displayedDimensions(file);
    return dimensions && snapAspectRatio(dimensions);
}

/** A short label: the common ratio's name, or the exact ratio to 1 ("2,35:1", "1:2,35") for any other. */
export function formatAspectRatioShort(dimensions: Dimensions): string {
    const named = namedRatio(dimensions);
    if (named) return named.name;
    const {width, height} = dimensions;
    const exact = (Math.max(width, height) / Math.min(width, height)).toLocaleString('hu', {maximumFractionDigits: 2});
    return width > height ? `${exact}:1` : `1:${exact}`;
}

/** "fekvő" (landscape), "álló" (portrait) or "négyzet" (square) for a value of `snapAspectRatio`. */
export function aspectRatioOrientation(value: string): string {
    if (value === OTHER_LANDSCAPE_RATIO) return 'fekvő';
    if (value === OTHER_PORTRAIT_RATIO) return 'álló';
    const [w, h] = value.split(':').map(Number);
    return w > h ? 'fekvő' : w < h ? 'álló' : 'négyzet';
}

/** A value of `snapAspectRatio` with its orientation: "3:2 (fekvő)", "Egyéb álló". */
export function formatAspectRatio(value: string): string {
    if (value === OTHER_LANDSCAPE_RATIO) return 'Egyéb fekvő';
    if (value === OTHER_PORTRAIT_RATIO) return 'Egyéb álló';
    return `${value} (${aspectRatioOrientation(value)})`;
}

/** Orders values of `snapAspectRatio`: landscape first (common ratios, then "other"), square, then portrait. */
export function compareAspectRatios(a: string, b: string): number {
    return aspectRatioRank(a) - aspectRatioRank(b);
}

function aspectRatioRank(value: string): number {
    const orientationRank = {'fekvő': 0, 'négyzet': 1, 'álló': 2}[aspectRatioOrientation(value)] ?? 3;
    const index = NAMED_RATIOS.findIndex((r) => r.name === value);
    return orientationRank * 100 + (index === -1 ? 99 : index);
}
